import React, { useState } from 'react';
import {
  Pencil, ToggleLeft, ToggleRight, Trash2, UploadCloud, Loader2, ImageIcon,
  Square, CheckSquare, ChevronRight, ChevronDown, Plus, Layers,
} from 'lucide-react';
import ProductMarginLine from './ProductMarginLine';

// Stock badge shared by parent + variant rows.
function StockBadge({ product }) {
  const out = product.in_stock === false || (product.stock_quantity != null && product.stock_quantity <= 0);
  const low = !out && product.stock_quantity != null && product.stock_quantity <= 3;
  if (out) return <span className="text-[10px] font-bold bg-white/5 text-white/30 px-2 py-0.5">OUT</span>;
  if (low) return <span className="text-[10px] font-bold bg-yellow-400/10 text-yellow-400 px-2 py-0.5">{product.stock_quantity}</span>;
  if (product.stock_quantity != null) return <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5">{product.stock_quantity}</span>;
  return <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5">∞</span>;
}

// The VAT rate this product's till lines and Sage export row carry. Standard is
// the norm so it stays quiet; a zero-rated or reduced line is highlighted, so a
// wrongly rated food line is spottable at a glance.
function VatBadge({ product }) {
  const rate = product.vat_rate || 'T20';
  const tone = rate === 'T0'
    ? 'bg-emerald-500/10 text-emerald-400'
    : rate === 'T5' ? 'bg-amber-400/10 text-amber-400' : 'bg-white/5 text-white/40';
  return <span className={`text-[10px] font-bold px-2 py-0.5 mt-0.5 inline-block ${tone}`}>VAT {rate}</span>;
}

function Thumb({ product }) {
  return (
    <div className="w-10 h-10 bg-[#0d0d0f] border border-white/5 flex items-center justify-center shrink-0 overflow-hidden">
      {product.image_url
        ? <img src={product.image_url} alt="" className="w-full h-full object-cover" />
        : <ImageIcon className="w-4 h-4 text-white/20" />}
    </div>
  );
}

// A single 12-col action cluster for one product (edit / push / toggle / delete).
function RowActions({ product, onEdit, onPush, onToggleStock, onDelete, pushingId }) {
  const out = product.in_stock === false || (product.stock_quantity != null && product.stock_quantity <= 0);
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => onEdit(product)} className="p-2 text-white/30 hover:text-white border border-white/5 hover:border-white/20 transition-all" title="Edit">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onPush(product)}
        disabled={pushingId === product.id}
        className="p-2 text-white/30 hover:text-primary border border-white/5 hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        title={product.is_variant
          ? 'Send this variation\u2019s cost price to Square'
          : (product.square_item_id ? 'Update this item in Square' : 'Create this item in Square')}
      >
        {pushingId === product.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
      </button>
      <button onClick={() => onToggleStock(product)} className={`p-2 border transition-all ${out ? 'text-white/30 border-white/5 hover:border-primary/40 hover:text-primary' : 'text-primary border-primary/20 hover:border-primary/60'}`} title={out ? 'Mark in stock' : 'Mark out of stock'}>
        {out ? <ToggleLeft className="w-3.5 h-3.5" /> : <ToggleRight className="w-3.5 h-3.5" />}
      </button>
      <button onClick={() => onDelete(product)} className="p-2 text-white/20 hover:text-red-400 border border-white/5 hover:border-red-400/30 transition-all" title="Delete">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// One variant sub-row, shown indented under an expanded group.
function VariantRow({ product, selected, onToggleSelect, onEdit, onPush, onToggleStock, onDelete, pushingId }) {
  const isSelected = selected.has(product.id);
  return (
    <div className={`flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:gap-4 px-4 sm:pl-12 sm:pr-6 py-3 border-b border-white/5 bg-black/20 items-stretch sm:items-center transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-white/[0.02]'}`}>
      <div className="sm:col-span-1 flex items-center">
        <button onClick={() => onToggleSelect(product.id)} className="text-white/30 hover:text-white transition-colors">
          {isSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
        </button>
      </div>
      <div className="sm:col-span-4 flex items-center gap-3 min-w-0">
        <Thumb product={product} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-sm font-bold truncate">{product.option_value || product.variation_name || 'Variant'}</span>
            <span className="text-[9px] font-bold tracking-widest text-white/30 bg-white/5 px-1.5 py-0.5 uppercase">{product.option_name || 'Option'}</span>
          </div>
          <p className="text-white/30 text-[11px] font-mono truncate">{product.sku || '— no SKU —'}</p>
        </div>
      </div>
      <p className="hidden sm:block sm:col-span-2 text-white/20 text-xs">—</p>
      <div className="sm:col-span-2">
        <p className="text-white font-bold text-sm">£{product.price?.toFixed(2)}</p>
        <ProductMarginLine product={product} />
        <VatBadge product={product} />
      </div>
      <div className="sm:col-span-1"><StockBadge product={product} /></div>
      <div className="sm:col-span-2"><RowActions product={product} onEdit={onEdit} onPush={onPush} onToggleStock={onToggleStock} onDelete={onDelete} pushingId={pushingId} /></div>
    </div>
  );
}

export default function ProductGroupRow({
  group,
  categoryLabel,
  selected,
  onToggleSelect,
  onToggleSelectGroup,
  onEdit,
  onDelete,
  onToggleStock,
  onPush,
  pushingId,
  onAddVariation,
}) {
  const [open, setOpen] = useState(false);
  const { parent, members, variants, isGroup } = group;

  // Price range across all members.
  const prices = members.map((m) => Number(m.price)).filter((n) => !Number.isNaN(n));
  const minP = prices.length ? Math.min(...prices) : null;
  const maxP = prices.length ? Math.max(...prices) : null;
  const priceText = isGroup
    ? (minP != null && maxP === minP ? `£${minP.toFixed(2)}` : minP != null ? `£${minP.toFixed(2)}–£${maxP.toFixed(2)}` : '—')
    : `£${parent.price?.toFixed(2)}`;

  // Aggregated stock for the group.
  const tracked = members.filter((m) => m.stock_quantity != null);
  const allTracked = tracked.length === members.length;
  const totalQty = tracked.reduce((s, m) => s + (Number(m.stock_quantity) || 0), 0);
  const groupOutOfStock = members.every((m) => m.in_stock === false || (m.stock_quantity != null && m.stock_quantity <= 0));

  // Group selection state.
  const groupIds = members.map((m) => m.id);
  const allSelected = groupIds.every((id) => selected.has(id));
  const someSelected = groupIds.some((id) => selected.has(id));
  const parentSelected = allSelected;

  return (
    <>
      {/* Parent row */}
      <div
        className={`flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:gap-4 px-4 sm:px-6 py-4 border-b border-white/5 items-stretch sm:items-center transition-colors ${someSelected ? 'bg-primary/5' : 'hover:bg-white/[0.02]'}`}
      >
        <div className="sm:col-span-1 flex items-center gap-2">
          {isGroup ? (
            <button onClick={() => setOpen((o) => !o)} className="text-white/40 hover:text-white transition-colors" title="Show variations">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : null}
          <button
            onClick={() => (isGroup ? onToggleSelectGroup(groupIds, !allSelected) : onToggleSelect(parent.id))}
            className="text-white/30 hover:text-white transition-colors"
            title={isGroup ? 'Select all variations' : 'Select product'}
          >
            {isGroup
              ? (allSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : someSelected ? <CheckSquare className="w-4 h-4 text-primary/50" /> : <Square className="w-4 h-4" />)
              : (parentSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />)}
          </button>
        </div>

        <div className="sm:col-span-4 flex items-center gap-3 min-w-0">
          <Thumb product={parent} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white text-sm font-bold truncate">{parent.name}</p>
              {isGroup && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 uppercase">
                  <Layers className="w-2.5 h-2.5" /> {members.length} variations
                </span>
              )}
              {parent.featured && <span className="text-primary text-[9px] font-bold tracking-widest">FEATURED</span>}
              {parent.published === false && <span className="text-white/30 text-[9px] font-bold tracking-widest border border-white/15 px-1 py-0.5">HIDDEN</span>}
              {parent.manual_product && <span className="text-cyan-400 text-[9px] font-bold tracking-widest">MANUAL</span>}
              {parent.ebay_listed && <span className="text-violet-400 text-[9px] font-bold tracking-widest">eBAY</span>}
              {parent.platform && parent.platform !== 'both' && <span className="text-white/40 text-[9px] font-bold tracking-widest uppercase">{parent.platform}</span>}
            </div>
            {isGroup && (
              <p className="text-white/30 text-[11px] mt-0.5">
                {Array.from(new Set(variants.map((m) => m.option_value).filter(Boolean))).join(' · ') || 'multiple options'}
              </p>
            )}
          </div>
        </div>

        <p className="sm:col-span-2 text-white/40 text-xs">{categoryLabel(parent)}</p>
        <div className="sm:col-span-2">
          <p className="text-white font-bold text-sm">{priceText}</p>
          {/* A group's price is a range, so the margin is only meaningful on a
              single product — each variation shows its own when expanded. */}
          {!isGroup && <ProductMarginLine product={parent} />}
          <VatBadge product={parent} />
        </div>
        <div className="sm:col-span-1">
          {isGroup ? (
            groupOutOfStock ? (
              <span className="text-[10px] font-bold bg-white/5 text-white/30 px-2 py-0.5">OUT</span>
            ) : allTracked ? (
              <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5">{totalQty}</span>
            ) : (
              <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5">∞</span>
            )
          ) : (
            <StockBadge product={parent} />
          )}
        </div>

        <div className="sm:col-span-2 flex items-center gap-1">
          {isGroup && (
            <button
              onClick={() => onAddVariation(parent)}
              className="p-2 text-primary border border-primary/20 hover:border-primary/60 transition-all"
              title="Add a variation (colour / SKU / price)"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          <RowActions product={parent} onEdit={onEdit} onPush={onPush} onToggleStock={onToggleStock} onDelete={onDelete} pushingId={pushingId} />
        </div>
      </div>

      {/* Expanded variation rows */}
      {isGroup && open && variants.map((v) => (
        <VariantRow
          key={v.id}
          product={v}
          selected={selected}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onPush={onPush}
          onToggleStock={onToggleStock}
          onDelete={onDelete}
          pushingId={pushingId}
        />
      ))}
    </>
  );
}